import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  giftDeliveryCategoryDataAtomFamily,
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryStatusAtomFamily,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import {
  createGiftDeliveryCategory,
  updateGiftDeliveryCategory,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.controller';
import { useGiftDeliveryCategoryHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `img-${Math.random().toString(36).slice(2)}`;

/** 폼 내부에서 다루는 이미지 항목 (기존 업로드 이미지 또는 새로 선택한 파일) */
interface ImageItem {
  key: string; // 렌더 안정용 로컬 키
  image: Image | null; // 기존 업로드 이미지(populated)
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
}

// 새 파일들을 ImageItem 으로 변환 (object URL 미리보기 생성)
const filesToImageItems = (files: File[]): ImageItem[] =>
  files.map((file) => ({
    key: genKey(),
    image: null,
    file,
    preview: URL.createObjectURL(file),
  }));

// 이미지 항목 순서 이동
const moveImageItem = (
  setter: Dispatch<SetStateAction<ImageItem[]>>,
  key: string,
  dir: -1 | 1,
) =>
  setter((prev) => {
    const idx = prev.findIndex((i) => i.key === key);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= prev.length) return prev;
    const arr = [...prev];
    const tmp = arr[idx] as ImageItem;
    arr[idx] = arr[next] as ImageItem;
    arr[next] = tmp;
    return arr;
  });

// 이미지 항목 제거 (새 파일이면 object URL 정리)
const removeImageItem = (setter: Dispatch<SetStateAction<ImageItem[]>>, key: string) =>
  setter((prev) => {
    const target = prev.find((i) => i.key === key);
    if (target?.preview) URL.revokeObjectURL(target.preview);
    return prev.filter((i) => i.key !== key);
  });

// 이미지 항목 표시 URL (새 파일 우선, 없으면 기존)
const imageItemUrl = (item: ImageItem, variant: 'raw' | 'w400') =>
  item.preview || (item.image ? fileBucketUrl(item.image.url, variant) : '');

export const GiftDeliveryCategoryFormPage: FC = function GiftDeliveryCategoryFormPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { sellerRole } = useAuth();

  const isEditMode = Boolean(categoryId);

  useGiftDeliveryCategoryHook(categoryId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftDeliveryCategoryStatusAtomFamily(categoryId ?? ''));
  const categoryData = useRecoilValue(
    giftDeliveryCategoryDataAtomFamily(categoryId ?? ''),
  );

  const setListData = useSetRecoilState(giftDeliveryCategoryListDataAtom);

  const [name, setName] = useState('');
  const [order, setOrder] = useState<number | ''>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  // 상세 이미지: 기존 이미지와 새 파일을 한 목록에서 함께 다룬다(추가/순서변경/삭제)
  const [detailImageItems, setDetailImageItems] = useState<ImageItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const detailImageOpenId = `deliveryCategoryDetailImage-${categoryId ?? 'new'}`;
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${detailImageOpenId}`),
  );

  useEffect(() => {
    if (isEditMode && categoryData) {
      setName(categoryData.name);
      setOrder(categoryData.order ?? '');
      if (Array.isArray(categoryData.detailImages)) {
        setDetailImageItems(
          (categoryData.detailImages as Image[]).map((img) => ({
            key: genKey(),
            image: img,
            file: null,
            preview: '',
          })),
        );
      }
    }
  }, [isEditMode, categoryData]);

  // 업로드한 새 이미지는 기존 목록 뒤에 추가(덮어쓰지 않음)
  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageItems((prev) => [...prev, ...filesToImageItems(files)]);
    setDetailImageUploaderOpen(false);
  };

  const defaultImageUrl = useMemo(() => {
    if (categoryData?.image && typeof categoryData.image === 'object') {
      return (categoryData.image as Image).url;
    }
    return undefined;
  }, [categoryData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('카테고리명을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      let imageId: string | undefined;
      if (imageFile) {
        const uploaded = await postImage(imageFile, sellerRole());
        imageId = uploaded._id;
      }

      // 상세 이미지: 기존+신규를 화면 순서대로 전송. 신규 파일은 업로드 후 id로 변환.
      // 수정 모드에서는 빈 배열도 전송해 전체 삭제/순서변경을 반영한다.
      const detailImageIds: string[] = [];
      for (const item of detailImageItems) {
        if (item.file) {
          const uploaded = await postImage(item.file, sellerRole());
          detailImageIds.push(uploaded._id);
        } else if (item.image) {
          detailImageIds.push(item.image._id);
        }
      }

      const payload = {
        name: name.trim(),
        ...(imageId && { image: imageId }),
        ...(order !== '' && { order: Number(order) }),
        ...(isEditMode
          ? { detailImages: detailImageIds }
          : detailImageIds.length > 0 && { detailImages: detailImageIds }),
      };

      if (isEditMode && categoryId) {
        const updated = await updateGiftDeliveryCategory(
          categoryId,
          payload,
          sellerRole(),
        );
        setListData((prev) =>
          prev.map((item) => (item._id === categoryId ? updated : item)),
        );
        alert('수정되었습니다.');
      } else {
        const created = await createGiftDeliveryCategory(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/delivery-category');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">
            배송 카테고리 {isEditMode ? '수정' : '등록'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4">
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="space-y-4">
            <TextField
              label="카테고리명"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 신선식품, 가전, 생활용품"
              required
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                표시 순서
              </label>
              <input
                type="number"
                value={order}
                onChange={(e) =>
                  setOrder(e.target.value === '' ? '' : Number(e.target.value))
                }
                min={0}
                placeholder="미설정"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">낮을수록 앞에 표시됩니다.</p>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold">썸네일 이미지</h2>
          <div className="max-w-[300px]">
            <ImageUploadBox
              title="썸네일 이미지"
              setFile={setImageFile}
              defaultImageUrl={defaultImageUrl}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            카테고리 목록에서 사용되는 대표 썸네일 이미지입니다.
          </p>
        </div>

        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="mb-1 text-base font-semibold">상세(맥락설명) 이미지</h2>
          <p className="mb-2 text-xs text-gray-500">
            이 카테고리에 속한 상품의 상품상세페이지에서, 상품 상세 이미지보다 먼저
            세로로 표시되는 맥락설명용 이미지입니다. 업로드하면 기존 이미지 뒤에
            추가되며, ▲▼ 로 순서를 바꿀 수 있습니다.
          </p>
          <div className="max-w-[400px]">
            <Button
              type="button"
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
              onClick={() => setDetailImageUploaderOpen(true)}
            >
              이미지 추가
            </Button>

            {detailImageItems.length > 0 ? (
              <div className="mt-2 space-y-1">
                {detailImageItems.map((item, idx) => (
                  <div key={item.key} className="relative">
                    <img
                      src={imageItemUrl(item, 'w400')}
                      alt={`상세이미지 ${idx + 1}`}
                      className="w-full rounded object-cover"
                    />
                    <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                      {idx + 1}
                    </span>
                    {item.file && (
                      <span className="absolute left-7 top-1 rounded bg-blue-500 px-1 text-[10px] text-white">
                        new
                      </span>
                    )}
                    <div className="absolute right-1 top-1 flex gap-0.5 rounded bg-black/50 px-0.5 py-0.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveImageItem(setDetailImageItems, item.key, -1)}
                        className="px-1 text-xs text-white disabled:text-white/30"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={idx === detailImageItems.length - 1}
                        onClick={() => moveImageItem(setDetailImageItems, item.key, 1)}
                        className="px-1 text-xs text-white disabled:text-white/30"
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImageItem(setDetailImageItems, item.key)}
                        className="px-1 text-xs text-red-300 hover:text-red-200"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-gray-400">
                등록된 상세 이미지가 없습니다.
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700"
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-indigo-600 py-3 font-medium text-white disabled:opacity-50"
          >
            {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
          </Button>
        </div>
      </form>

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={detailImageOpenId}
        title="상세 이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
